/**
 * useCampaignPublicController — controller UI de la page /c/[slug].
 *
 * Couche 2b (ADR-001) : controller riche qui orchestre :
 *   - Le détail campagne (chargé en SSR ou via service côté client)
 *   - La liste paginée de donateurs (cache TanStack par tri)
 *   - Le socket temps réel (`useCampaignSocket`) qui injecte les
 *     nouveaux dons dans le cache + le ref local + déclenche un toast
 *   - Les helpers d'affichage (formatters, lightbox galerie, share)
 *
 * Les appels HTTP vivent dans :
 *   - `~/services/api/campaignsPublic.ts` (transport)
 *   - `~/data/queries/public.queries.ts` (helper `fetchCampaignDonors`
 *     + key factory `campaignDonorsKey` réutilisée par setQueryData)
 *
 * Renommé depuis `useCampaignPublic` lors de Phase 3.3c (ADR-001 :
 * suffixe `Controller` obligatoire pour les composables qui orchestrent
 * fetch + UI riche). Compat conservée via re-export dans
 * `useCampaignPublic.ts`.
 */
import { formatRelativeTime, jourDeFinCagnotte } from "@donoor/utils";
import { formatInParis } from "@donoor/utils/dates";
import type { Campaign, CampaignDonor, TopDonor } from "~/types/campaign";
import { useCampaignSocket, type CampaignDonationData } from "~/composables/campaign/useCampaignSocket";
import { phraseDon, useAnnonceDerniereActivite, useToastEnDirect } from "~/composables/useToastEnDirect";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { campaignsPublicApi, type CampaignDonorsResponse } from "~/services/api/campaignsPublic";
import { useDemoAccueil, useDonneeDemoAccueil } from "~/composables/useDemoAccueil";
import {
  fetchCampaignDonors,
  campaignDonorsKey,
} from "~/data/queries/public.queries";

const DONORS_PER_PAGE = 10;

/**
 * Classe une erreur de chargement campagne en `{ errorType, message }`
 * pour l'affichage public (privée / introuvable / générique).
 */
type Translate = (key: string, params?: Record<string, unknown>) => string;

function classifyCampaignLoadError(err: unknown, t: Translate): {
  errorType: "not_found" | "private" | "generic";
  message: string;
} {
  const e = err as {
    status?: number;
    statusCode?: number;
    response?: { status?: number; _data?: { code?: string } };
    data?: { code?: string };
    message?: string;
  };
  const statusCode = e?.status || e?.statusCode || e?.response?.status;
  const errorCode = e?.data?.code || e?.response?._data?.code;

  if (statusCode === 403 || errorCode === "CAMPAIGN_PRIVATE") {
    return {
      errorType: "private",
      message: t("campagne.controllerPublic.erreurPrivee"),
    };
  }
  if (statusCode === 404) {
    return {
      errorType: "not_found",
      message: t("campagne.controllerPublic.erreurIntrouvable"),
    };
  }
  return {
    errorType: "generic",
    message: e.message || t("campagne.controllerPublic.erreurChargementGenerique"),
  };
}

export const useCampaignPublicController = (slug: string, ssrData?: Campaign | null) => {
  const toast = useToast();
  const { annoncerEnDirect } = useToastEnDirect();
  const config = useRuntimeConfig();
  const assetsBaseUrl = config.public.apiUrl;
  const { t } = useI18n();
  const { formatCurrency } = useFormatLangue();

  // Helper pour construire les URLs d'assets Directus.
  // `width` (optionnel) → transformation Directus à la volée (resize + webp +
  // qualité 80). SANS width, les images uploadées par les assos étaient servies
  // en pleine résolution (photos de 2-3 Mo) → LCP mobile catastrophique.
  // Le carousel hero s'affiche à ~800px max (desktop) : 1200px couvre le retina.
  // (Audit perf pages cagnotte 2026-07-31.)
  const getFileUrl = (
    fileId: string | null | undefined,
    width?: number,
  ): string => {
    if (!fileId) return "";
    if (fileId.startsWith("http")) return fileId;
    const base = `${assetsBaseUrl}/api/assets/${fileId}`;
    if (!width) return base;
    // quality 82 : marge de netteté pour les heros retina (1600px). Le poids
    // reste minime vs l'original brut (2-3 Mo → ~180 Ko).
    return `${base}?width=${width}&format=webp&quality=82&fit=cover`;
  };

  // Socket pour temps réel
  const {
    connect: connectSocket,
    joinCampaignRoom,
    onCampaignDonation,
    disconnect: disconnectSocket,
    isConnected: socketConnected,
  } = useCampaignSocket();

  // État principal — initialisé avec les données SSR si disponibles
  const campaign = ref<Campaign | null>(ssrData || null);

  // Dernier don (moins de 7 jours) annoncé à l'arrivée sur la page.
  useAnnonceDerniereActivite(
    () => campaign.value?.derniere_activite,
    // « Paul Moret vient de donner 20 € » (moins de 15 min), sinon « Paul
    // Moret a donné 20 € » ; « Quelqu'un… » sans nom public — le moment est
    // affiché dessous.
    (a, recente) => phraseDon(t, a.montant ? formatCurrency(a.montant, { decimals: 0 }) : null, recente, a.nom),
  );
  const isLoading = ref(!ssrData);
  const error = ref<string | null>(null);
  const errorType = ref<"not_found" | "private" | "generic" | null>(null);

  // Donateurs — TanStack Query pour cache instantané entre tris
  const queryClient = useQueryClient();
  const donorsSortBy = ref<"recent" | "highest">("recent");
  const donorsOffset = ref(0);
  const allDonors = ref<CampaignDonor[]>([]);
  const donorsTotal = ref(0);
  const isLoadingMoreDonors = ref(false);
  const topDonor = ref<TopDonor | null>(null);

  // Lightbox galerie
  const lightboxOpen = ref(false);
  const lightboxIndex = ref(0);

  // Onglet actif
  const activeInfoTab = ref<"description" | "updates">("description");

  // Nouveaux dons en temps réel
  const realtimeDonations = ref<CampaignDonationData[]>([]);

  async function loadCampaign() {
    // Si on a déjà les données SSR, on ne re-fetch pas — on connecte juste le socket
    // TanStack Query charge les donateurs automatiquement via `enabled`
    if (campaign.value) {
      isLoading.value = false;
      if (campaign.value.id) {
        setupRealtimeSocket(campaign.value.id);
      }
      return;
    }

    try {
      campaign.value = await campaignsPublicApi.bySlug(slug);
      if (campaign.value?.id) {
        setupRealtimeSocket(campaign.value.id);
      }
    } catch (err: unknown) {
      const classified = classifyCampaignLoadError(err, t);
      errorType.value = classified.errorType;
      error.value = classified.message;
    } finally {
      isLoading.value = false;
    }
  }

  function setupRealtimeSocket(campaignId: number | string) {
    connectSocket();
    joinCampaignRoom(campaignId);

    onCampaignDonation(applyIncomingDonation);
  }

  /**
   * Applique un don qui ARRIVE : montant, progression, liste des donateurs,
   * meilleur donateur, notification.
   *
   * ⚠️ Extrait du gestionnaire de socket pour être appelable AUTREMENT que par
   * le socket — l'aperçu du dashboard s'en sert pour simuler un don
   * (`preview-donation`), et voit donc EXACTEMENT ce que verra un visiteur au
   * moment d'un vrai don. Simuler dans un chemin séparé aurait produit une
   * démonstration qui ment. Karim 2026-08-30.
   */
  function applyIncomingDonation(data: CampaignDonationData) {
      if (campaign.value) {
        campaign.value.raised = data.newTotalAmount;
        campaign.value.donors_count = data.donorCount;

        if (campaign.value.goal_amount && campaign.value.goal_amount > 0) {
          campaign.value.percent = Math.round(
            (data.newTotalAmount / campaign.value.goal_amount) * 100
          );
        }
      }

      // Injecter le nouveau don dans le cache TanStack + refs locales
      if (campaign.value?.show_donors_list) {
        const newDonor: CampaignDonor = {
          id: data.donationId,
          name: data.donorName || t("campagne.controllerPublic.anonyme"),
          amount: data.amount,
          is_anonymous: data.isAnonymous,
          message: data.message,
          created_at: data.timestamp,
        };

        // Ajouter en tête si tri par récent
        if (donorsSortBy.value === "recent") {
          allDonors.value.unshift(newDonor);
          donorsTotal.value++;
        }

        // Mettre à jour le cache TanStack pour le tri "recent" (le don est toujours le plus récent)
        queryClient.setQueryData(
          campaignDonorsKey(campaign.value.id, "recent"),
          (old: { data?: CampaignDonor[]; pagination?: { total?: number } } | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: [newDonor, ...(old.data || [])],
              pagination: { ...old.pagination, total: (old.pagination?.total || 0) + 1 },
            };
          }
        );

        // Invalider le cache "highest" pour qu'il se refetch en arrière-plan au prochain switch
        queryClient.invalidateQueries({ queryKey: campaignDonorsKey(campaign.value.id, "highest") });

        if (topDonor.value && data.amount > topDonor.value.amount) {
          topDonor.value = {
            id: data.donationId,
            name: data.donorName || t("campagne.controllerPublic.anonyme"),
            amount: data.amount,
            is_anonymous: data.isAnonymous,
            created_at: data.timestamp,
          };
        }
      }

      realtimeDonations.value.push(data);
      // Le socket envoie le nom de tout don non anonyme : il n'est affiché
      // que si la cagnotte publie ses donateurs (Karim 2026-10-22).
      showDonationToast(data.amount, campaign.value?.show_donors_list ? data.donorName : null);
  }

  function showDonationToast(amount: number, nom: string | null) {
    annoncerEnDirect(phraseDon(t, formatCurrency(amount, { decimals: 0 }), true, nom));
  }

  onUnmounted(() => {
    disconnectSocket();
  });

  // TanStack Query pour les donateurs — cache par tri, switch instantané
  const campaignId = computed(() => campaign.value?.id);
  const donorsQueryKey = computed(() => campaignDonorsKey(campaignId.value, donorsSortBy.value));

  // Démo de la home, rendue par le serveur avec elle (cf. useDemoAccueil) :
  // la 1re page de donateurs est chargée AU RENDU, comme la cagnotte, puis
  // remise dans le cache des donateurs quand la démo démarre. Sans ça, la
  // section « Donateurs » et son entrée du sommaire apparaissaient d'un coup
  // au démarrage. La vraie page cagnotte, elle, ne change pas.
  const idDemo = campaign.value?.id;
  if (useDemoAccueil().estIntegree && idDemo && campaign.value?.show_donors_list) {
    // Pas de relecture au démarrage : la liste se met à jour d'elle-même (useQuery ci-dessous).
    const donnee = useDonneeDemoAccueil(`campaign-donors-${idDemo}`, { relire: false });
    const { data: donateursRendus } = useAsyncData(
      donnee.key,
      async () => {
        try {
          const reponse = await $fetch<CampaignDonorsResponse>(
            `${config.public.apiBaseUrl}/api/campaigns/public/${idDemo}/donors`,
            { query: { limit: DONORS_PER_PAGE, offset: 0, sortBy: "recent" }, timeout: 8000 },
          );
          if (import.meta.server) appliquerDonateurs(reponse);
          return reponse;
        } catch {
          // La démo démarrera sans : les donateurs arrivent alors comme avant.
          return null;
        }
      },
      // Navigateur : jamais de requête ici (useQuery s'en charge), seulement
      // la reprise du rendu du serveur.
      { getCachedData: donnee.getCachedData, immediate: import.meta.server },
    );
    // Rangée comme « ancienne » (`updatedAt: 0`) : useQuery la relit aussitôt
    // en arrière-plan, la liste devient celle du moment sans rien vider.
    if (import.meta.client && donateursRendus.value) {
      queryClient.setQueryData(campaignDonorsKey(idDemo, "recent"), donateursRendus.value, { updatedAt: 0 });
    }
  }

  const { data: donorsData, isLoading: isLoadingDonorsQuery } = useQuery({
    queryKey: donorsQueryKey,
    queryFn: async ({ signal }) => {
      if (!campaignId.value) return null;
      return fetchCampaignDonors(
        campaignId.value,
        {
          limit: DONORS_PER_PAGE,
          offset: 0,
          sortBy: donorsSortBy.value,
        },
        signal,
      );
    },
    enabled: computed(() => !!campaignId.value && !!campaign.value?.show_donors_list),
    staleTime: 60 * 1000, // 1min
    placeholderData: (prev) => prev, // Garde les données précédentes pendant le refetch (switch de tri instantané)
  });

  // Synchroniser les données TanStack → refs réactives (pour compat avec les composants)
  function appliquerDonateurs(data: CampaignDonorsResponse | null | undefined) {
    if (!data) return;
    allDonors.value = data.data || [];
    donorsTotal.value = data.pagination?.total || 0;
    topDonor.value = data.topDonor || null;
    // eslint-disable-next-line no-restricted-syntax -- offset cursor local pagination load-more (pas un total)
    donorsOffset.value = allDonors.value.length;
  }
  watch(donorsData, appliquerDonateurs, { immediate: true });

  // eslint-disable-next-line no-restricted-syntax -- count items chargés en local (load-more), total backend via donorsTotal
  const isLoadingDonors = computed(() => isLoadingDonorsQuery.value && allDonors.value.length === 0);

  // eslint-disable-next-line no-restricted-syntax -- compare items chargés vs total backend pour pagination load-more
  const hasMoreDonors = computed(() => allDonors.value.length < donorsTotal.value);

  async function loadMoreDonors() {
    if (isLoadingMoreDonors.value || !hasMoreDonors.value || !campaignId.value) return;
    isLoadingMoreDonors.value = true;

    try {
      const res = await fetchCampaignDonors(campaignId.value, {
        limit: DONORS_PER_PAGE,
        // eslint-disable-next-line no-restricted-syntax -- offset cursor local pagination load-more
        offset: allDonors.value.length,
        sortBy: donorsSortBy.value,
      });

      allDonors.value = [...allDonors.value, ...(res.data || [])];
      donorsTotal.value = res.pagination?.total || 0;
    } catch {
      // Erreur silencieuse
    } finally {
      isLoadingMoreDonors.value = false;
    }
  }

  // === Computed ===

  const campaignImageUrl = computed(() => {
    if (!campaign.value?.image) return null;
    // 1200px : carousel hero (affiché ~800px max desktop, retina couvert).
    return getFileUrl(campaign.value.image, 1600);
  });

  const galleryImages = computed(() => {
    if (!campaign.value?.gallery || campaign.value.gallery.length === 0) return [];
    return campaign.value.gallery.map((item) => ({
      id: item.id,
      url: getFileUrl(item.directus_files_id, 1600),
    }));
  });

  function extractYouTubeId(url: string | null | undefined): string | null {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1] ?? null;
    }
    return null;
  }

  const youtubeEmbedUrl = computed(() => {
    const id = extractYouTubeId(campaign.value?.youtube_url);
    return id ? `https://www.youtube.com/embed/${id}` : null;
  });

  const progressPercent = computed(() => {
    if (!campaign.value) return 0;
    if (campaign.value.goal_amount && campaign.value.goal_amount > 0) {
      const percent = Math.round((campaign.value.raised || 0) / campaign.value.goal_amount * 100);
      return Math.min(100, percent);
    }
    return Math.min(100, campaign.value.percent || 0);
  });

  const launchDateText = computed(() => {
    if (!campaign.value?.start_date) return null;
    // Dates campagne affichées EN HEURE DE PARIS (fuseau métier) : instants UTC
    // → sans timeZone forcé, le jour affiché dériverait du fuseau navigateur.
    // NOTE i18n : formatInParis (@donoor/utils/dates, packages/* hors scope)
    // formate la date — à vérifier qu'il suit bien la locale demandée, sinon
    // le jour/mois reste en français même sur /en, /de, /es, /it, /nl.
    return t("campagne.controllerPublic.lanceeLe", {
      date: formatInParis(campaign.value.start_date, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    });
  });

  const hasUpdates = computed(() => {
    return !!(campaign.value?.campaign_updates && campaign.value.campaign_updates.length > 0);
  });

  const isCampaignClosed = computed(() => campaign.value?.status === "completed");
  // "paused" = en pause (cagnotte publiée mais arrêtée), "draft" = brouillon
  // (aperçu admin). Les deux affichent un bandeau "non disponible au don".
  const isCampaignPaused = computed(
    () =>
      campaign.value?.status === "paused" ||
      campaign.value?.status === "draft",
  );

  /**
   * Pourquoi la cagnotte n'accepte pas de don, DISTINGUÉ (Karim 2026-10-15) :
   * la nouvelle page affiche « terminée », « en pause » ou « en
   * préparation » à la place du choix du montant. `null` = dons ouverts.
   */
  const etatDonCagnotte = computed<"terminee" | "pause" | "brouillon" | null>(() => {
    const s = campaign.value?.status;
    if (s === "completed" || s === "closed") return "terminee";
    if (s === "paused") return "pause";
    if (s === "draft") return "brouillon";
    return null;
  });

  /**
   * Compte à rebours (Karim 2026-09-30) : l'instant de clôture vient de
   * l'API (`closes_at`, minuit à la fin du jour de fin, heure de
   * l'association) ; le jour en clair sert à la phrase sous le compte.
   */
  const closesAt = computed(() => {
    const c = campaign.value;
    if (!c || c.status !== "active" || !c.closes_at) return null;
    return c.closes_at;
  });

  const endDateLabel = computed(() => {
    const jour = jourDeFinCagnotte(campaign.value?.end_date);
    if (!jour) return null;
    const memeAnnee = jour.slice(0, 4) === String(new Date().getFullYear());
    return formatInParis(jour, {
      day: "numeric",
      month: "long",
      ...(memeAnnee ? {} : { year: "numeric" }),
    });
  });

  /**
   * Le compte à rebours vient d'arriver à zéro : la page passe en
   * « clôturée » sur-le-champ (bandeau, boutons de don retirés), sans
   * attendre un rechargement — l'API refuse déjà tout don depuis minuit.
   */
  function marquerTerminee() {
    if (!campaign.value || campaign.value.status !== "active") return;
    campaign.value = { ...campaign.value, status: "completed", closes_at: null, days_remaining: null };
  }

  const closedDateText = computed(() => {
    if (!campaign.value || campaign.value.status !== "completed") return null;
    // La date de fin est un JOUR (« 2026-12-31 », renvoyé « 2026-12-31T00:00:00 ») :
    // lu comme tel, jamais comme un instant à l'heure du navigateur — sinon
    // un visiteur à l'ouest de Paris lisait la veille.
    const jourDeFin = jourDeFinCagnotte(campaign.value.end_date);
    return formatInParis(jourDeFin ?? new Date(), {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  });

  const hasGoal = computed(() => {
    return !!(campaign.value?.goal_amount && campaign.value.goal_amount > 0);
  });

  const campaignShareUrl = computed(() => {
    if (!campaign.value) return "";
    if (typeof window !== "undefined") {
      return `${window.location.origin}/c/${campaign.value.slug}`;
    }
    return `https://donoor.org/c/${campaign.value.slug}`;
  });

  const organizationLogo = computed(() => {
    const logo = campaign.value?.organization_logo;
    // Logo affiché en petit (avatar ~40-80px) : 200px suffit largement.
    return logo ? getFileUrl(logo, 200) : null;
  });

  const organizationName = computed(() => {
    return campaign.value?.organization_name || t("campagne.donationData.organisationParDefaut");
  });

  const organizationSlug = computed(() => {
    return campaign.value?.organization_slug || null;
  });

  // === Fonctions utilitaires ===

  function formatAmountShort(amount: number) {
    return formatCurrency(amount, { decimals: 0 });
  }
  // NOTE i18n (lot O1, 2026-10-10) : `formatAmountCompact`/`formatCountCompact`
  // ci-dessous construisent leur chaîne à la main ("1.5M €", "2.3k") sans passer
  // par formatCurrency/useFormatLangue — suffixes k/M et position du "€" restent
  // donc au format FR dans les 5 langues cibles. Hors scope de ce lot (règle :
  // ne pas toucher à la logique/aux calculs) ; signalé dans le rapport pour un
  // lot de suivi dédié au formatage des nombres compacts.

  // Format compact pour les grands nombres : 2345 → 2.3k, 1500000 → 1.5M
  function formatAmountCompact(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return "";
    const n = Number(amount);
    if (!Number.isFinite(n)) return "";
    if (n >= 1_000_000) {
      const m = n / 1_000_000;
      const formatted = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace(".0", "");
      return `${formatted}M €`;
    }
    if (n >= 1_000) {
      const k = n / 1_000;
      const formatted = k % 1 === 0 ? k.toFixed(0) : k.toFixed(1).replace(".0", "");
      return `${formatted}k €`;
    }
    return `${Math.round(n)} €`;
  }

  // Format compact sans devise (pour les comptes : 1234 → 1.2k, 1500000 → 1.5M)
  function formatCountCompact(count: number | null | undefined): string {
    if (count === null || count === undefined) return "0";
    const n = Number(count);
    if (!Number.isFinite(n)) return "0";
    if (n >= 1_000_000) {
      const m = n / 1_000_000;
      return m % 1 === 0 ? `${m.toFixed(0)}M` : `${m.toFixed(1).replace(".0", "")}M`;
    }
    if (n >= 1_000) {
      const k = n / 1_000;
      return k % 1 === 0 ? `${k.toFixed(0)}k` : `${k.toFixed(1).replace(".0", "")}k`;
    }
    return `${Math.round(n)}`;
  }

  function getRelativeTime(dateString: string) {
    // Source unique : style public Donoor (capitalisé + fallback date courte
    // au-delà de 7 jours). Cf. @donoor/utils formatRelativeTime.
    return formatRelativeTime(dateString, {
      style: "capitalized",
      dateFallbackAfterDays: 7,
    });
  }

  function formatUpdateDate(dateString: string): string {
    const date = new Date(dateString);
    // Date + heure du post EN HEURE DE PARIS (fuseau métier).
    return t("campagne.controllerPublic.posteLe", {
      date: formatInParis(date, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      heure: formatInParis(date, { hour: "2-digit", minute: "2-digit" }).replace(":", "h"),
    });
  }

  // === Lightbox ===

  function openLightbox(index: number) {
    lightboxIndex.value = index;
    lightboxOpen.value = true;
  }

  function closeLightbox() {
    lightboxOpen.value = false;
  }

  function prevImage() {
    if (lightboxIndex.value > 0) {
      lightboxIndex.value--;
    } else {
      lightboxIndex.value = galleryImages.value.length - 1;
    }
  }

  function nextImage() {
    if (lightboxIndex.value < galleryImages.value.length - 1) {
      lightboxIndex.value++;
    } else {
      lightboxIndex.value = 0;
    }
  }

  // === Partage ===

  async function copyShareLink() {
    if (!campaign.value) return;
    try {
      await navigator.clipboard.writeText(campaignShareUrl.value);
      toast.add({
        title: t("campagne.controllerPublic.lienCopieTitre"),
        description: t("campagne.controllerPublic.lienCopieDescription"),
        color: "success",
        icon: "i-lucide-check-circle",
      });
    } catch {
      toast.add({
        title: t("campagne.donationData.erreurTitreDefaut"),
        description: t("campagne.controllerPublic.erreurCopieDescription"),
        color: "error",
        icon: "i-lucide-alert-circle",
      });
    }
  }

  function shareOnFacebook() {
    const url = encodeURIComponent(campaignShareUrl.value);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank", "width=600,height=400");
  }

  function shareOnTwitter() {
    const url = encodeURIComponent(campaignShareUrl.value);
    const text = encodeURIComponent(t("campagne.controllerPublic.soutenezCetteCampagne", { titre: campaign.value?.title || "" }));
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, "_blank", "width=600,height=400");
  }

  function shareOnWhatsApp() {
    const url = encodeURIComponent(campaignShareUrl.value);
    const text = encodeURIComponent(`${t("campagne.controllerPublic.soutenezCetteCampagne", { titre: campaign.value?.title || "" })} - `);
    window.open(`https://wa.me/?text=${text}${url}`, "_blank");
  }

  function shareOnInstagram() {
    navigator.clipboard.writeText(campaignShareUrl.value);
    window.open("https://www.instagram.com/", "_blank");
  }

  function shareOnTelegram() {
    const url = encodeURIComponent(campaignShareUrl.value);
    const text = encodeURIComponent(t("campagne.controllerPublic.soutenezCetteCampagne", { titre: campaign.value?.title || "" }));
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
  }

  function shareByEmail() {
    if (!campaign.value) return;
    const subject = encodeURIComponent(t("campagne.controllerPublic.soutenezCetteCampagne", { titre: campaign.value.title }));
    const body = encodeURIComponent(
      t("campagne.controllerPublic.emailCorps", { titre: campaign.value.title, url: campaignShareUrl.value })
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  return {
    campaign,
    /** Applique un don entrant — appelé par le socket, et par l'aperçu simulé. */
    applyIncomingDonation,
    isLoading,
    error,
    errorType,
    donorsSortBy,
    allDonors,
    donorsTotal,
    isLoadingDonors,
    isLoadingMoreDonors,
    topDonor,
    hasMoreDonors,
    lightboxOpen,
    lightboxIndex,
    activeInfoTab,
    realtimeDonations,
    socketConnected,
    campaignImageUrl,
    galleryImages,
    youtubeEmbedUrl,
    progressPercent,
    launchDateText,
    hasUpdates,
    isCampaignClosed,
    isCampaignPaused,
    etatDonCagnotte,
    closedDateText,
    closesAt,
    endDateLabel,
    marquerTerminee,
    hasGoal,
    campaignShareUrl,
    organizationLogo,
    organizationName,
    organizationSlug,
    loadCampaign,
    loadMoreDonors,
    openLightbox,
    closeLightbox,
    prevImage,
    nextImage,
    formatAmountShort,
    formatAmountCompact,
    formatCountCompact,
    getRelativeTime,
    formatUpdateDate,
    copyShareLink,
    shareOnFacebook,
    shareOnTwitter,
    shareOnWhatsApp,
    shareOnInstagram,
    shareOnTelegram,
    shareByEmail,
  };
};
